import { z } from 'zod';
import { Appointment } from './appointments.js';
import { IsoDateTime, Pence, Uuid } from '../primitives.js';

/**
 * WS7 phase 3 — recurring appointment series (the standing Tuesday cut): every N weeks × M
 * occurrences for one staff member / service / (optional) client. A series carries renterId,
 * mirroring the owning staff member's book, so a sealed renter's standing weekly client is sealed
 * identically to the single appointments the series spawns.
 *
 * Creation is conflict-aware: each occurrence is tested against the availability engine and we report
 * BOTH the instances that fitted (real appointments) and the ones that clashed (never silently
 * dropped). The salon then rebooks/skips the clashes deliberately.
 */

/** Lifecycle of a series. 'cancelled' once cancel-remaining has run (kept for audit). */
export const SeriesStatus = z.enum(['active', 'cancelled']);
export type SeriesStatus = z.infer<typeof SeriesStatus>;

export const AppointmentSeries = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable(),
    locationId: Uuid.nullable(),
    staffId: Uuid,
    clientId: Uuid.nullable().describe('null for an anonymous standing booking'),
    serviceId: Uuid,
    clientDisplayName: z.string().max(160).describe('denormalised for floor visibility'),
    firstStartAt: IsoDateTime.describe('start of the first occurrence (UTC instant)'),
    intervalWeeks: z.int().min(1).max(26).describe('weeks between occurrences (e.g. 1, 2, 4)'),
    occurrenceCount: z.int().min(1).max(52).describe('total occurrences requested'),
    notes: z.string().max(2000).nullable(),
    status: SeriesStatus,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'AppointmentSeries' });
export type AppointmentSeries = z.infer<typeof AppointmentSeries>;

/**
 * Create a standing series. End time is derived from the service (like a single appointment); the
 * caller supplies only the first start, the cadence and the count.
 */
export const CreateSeries = z
  .object({
    staffId: Uuid,
    serviceId: Uuid,
    clientId: Uuid.nullish(),
    clientDisplayName: z.string().max(160).optional(),
    firstStartAt: IsoDateTime,
    intervalWeeks: z.int().min(1).max(26).default(1),
    occurrenceCount: z.int().min(1).max(52),
    notes: z.string().max(2000).nullish(),
    depositPaid: Pence.default(0).describe('deposit attached to each created occurrence'),
  })
  .meta({ id: 'CreateSeries' });
export type CreateSeries = z.infer<typeof CreateSeries>;

/**
 * One projected occurrence that the availability engine rejected: the wall-clock instant it would
 * have occupied plus the reason. Returned alongside the fitted appointments so the salon sees exactly
 * which standing slots couldn't be honoured (the staff member is off, the salon is shut, or another
 * booking already holds the chair).
 */
export const SeriesConflict = z
  .object({
    occurrenceIndex: z.int().min(0).describe('0-based position in the requested cadence'),
    startAt: IsoDateTime,
    endAt: IsoDateTime,
    reason: z
      .enum(['unavailable', 'overlap'])
      .describe(
        '"unavailable" = closed/off/outside hours; "overlap" = an existing booking clashes',
      ),
  })
  .meta({ id: 'SeriesConflict' });
export type SeriesConflict = z.infer<typeof SeriesConflict>;

/**
 * Result of creating a series: the series row, the occurrences that were created as real
 * appointments, and the occurrences that could NOT be fitted (a warning, never a silent drop).
 */
export const CreateSeriesResult = z
  .object({
    series: AppointmentSeries,
    created: z.array(Appointment).describe('occurrences that fitted and were booked'),
    conflicts: z.array(SeriesConflict).describe('occurrences that could not be fitted'),
  })
  .meta({ id: 'CreateSeriesResult' });
export type CreateSeriesResult = z.infer<typeof CreateSeriesResult>;

/**
 * Edit-series (apply forward): change the staff/service/time-of-day/cadence for every FUTURE,
 * not-yet-started occurrence. Past and in-progress occurrences are never touched. Any field omitted
 * is left unchanged. A change of cadence (intervalWeeks/occurrenceCount) regenerates the future tail.
 */
export const EditSeries = z
  .object({
    staffId: Uuid.optional(),
    serviceId: Uuid.optional(),
    /** New wall-clock start for the next occurrence; the cadence re-anchors to it. */
    firstStartAt: IsoDateTime.optional(),
    intervalWeeks: z.int().min(1).max(26).optional(),
    occurrenceCount: z.int().min(1).max(52).optional(),
    notes: z.string().max(2000).nullish(),
    clientDisplayName: z.string().max(160).optional(),
  })
  .meta({ id: 'EditSeries' });
export type EditSeries = z.infer<typeof EditSeries>;

/** Apply-forward result: the updated series plus the re-fitted / re-conflicted future occurrences. */
export const EditSeriesResult = z
  .object({
    series: AppointmentSeries,
    created: z.array(Appointment).describe('future occurrences re-fitted and re-booked'),
    conflicts: z.array(SeriesConflict).describe('future occurrences that could not be re-fitted'),
  })
  .meta({ id: 'EditSeriesResult' });
export type EditSeriesResult = z.infer<typeof EditSeriesResult>;

/**
 * Detach a single occurrence from its series ("edit this one"): the appointment keeps its booking but
 * its series_id is cleared so a later edit-series / cancel-remaining no longer touches it. The
 * detached appointment is then edited via the normal PATCH /appointments/:id path.
 */
export const DetachOccurrence = z
  .object({
    appointmentId: Uuid.describe('the occurrence to detach from the series'),
  })
  .meta({ id: 'DetachOccurrence' });
export type DetachOccurrence = z.infer<typeof DetachOccurrence>;

/** Optional filter for listing a salon's series (defaults to active only). */
export const SeriesQuery = z.object({
  staffId: Uuid.optional(),
  status: SeriesStatus.optional(),
});
export type SeriesQuery = z.infer<typeof SeriesQuery>;
